import { AnimationController } from 'providers/animation-controller';
import { animationFrame } from 'services/browser-utils';

/**
 * Shared capture animations for the AI scanner pages (owner decision
 * #17 in design-docs/scanner-shared-components.md: the label scanner's
 * photo animations move into the shared layer so every multi-photo
 * scanner gets them).
 *
 * Two moves, both built on a fixed-position <img> clone that flies
 * between two screen rectangles (translate + scale):
 * - snap:    camera preview -> the photo's reel slot
 * - collage: every reel thumbnail -> its collage position
 *
 * (There is deliberately no remove/fly-back animation: on native iOS
 * the camera preview is a native layer ABOVE the WebView, so a DOM
 * clone flying over the camera area is invisible. Photo removal is
 * instant instead.)
 *
 * A page calls `animateCapturedPhotoToReelAsync` or
 * `animateCollageTransitionAsync` and nothing else. Those two own the
 * whole move — the animation-frame waits, the destroyed-view checks,
 * stopping the camera, and the repaints — and the page lends them its
 * elements and its `viewDestroyed` / `markForCheck` through
 * `ScannerPageAnimationHooks`. The clone-fly primitives underneath are
 * module-private so a page cannot reach past them and hand-write the
 * sequence again. Element getters come from the shared components:
 * `cs-scanner-photo-reel.slotElements` and
 * `cs-scanner-camera-stage.collagePhotoElements`.
 */

/** Flies one photo clone from a captured rect to a target element. */
async function animateSnapToReelAsync(
  animationController: AnimationController,
  options: { cameraEl: HTMLElement | null; slotEl: HTMLElement | null; dataUrl: string },
) {
  const { cameraEl, slotEl, dataUrl } = options;
  if (!cameraEl || !slotEl) return;

  const fromRect = cameraEl.getBoundingClientRect();
  const toRect = slotEl.getBoundingClientRect();

  const clone = createAnimationClone(dataUrl, fromRect);
  // hide the real reel thumbnail during the animation
  slotEl.style.opacity = '0';

  await animationController.withAnimationTracking(async () => {
    const animation = animateCloneToRect(clone, fromRect, toRect, 250);
    await animationController.safeAwaitAnimations([animation]);
  });

  clone.remove();
  slotEl.style.opacity = '';
}

/**
 * Flies the reel thumbnails to their collage positions. `fromRects`
 * must be captured BEFORE the page switches the view (the reel may
 * leave the DOM when the collage appears). Returns false when the
 * collage elements don't match the photos — the caller just reveals
 * the collage without an animation in that case.
 */
async function animateReelToCollageAsync(
  animationController: AnimationController,
  options: {
    photos: { dataUrl: string }[];
    fromRects: DOMRect[];
    collagePhotoEls: HTMLElement[];
    reelSlotEls: HTMLElement[];
  },
): Promise<boolean> {
  const { photos, fromRects, collagePhotoEls, reelSlotEls } = options;
  if (collagePhotoEls.length !== photos.length || collagePhotoEls.length === 0) {
    return false;
  }

  const toRects = collagePhotoEls.map(el => el.getBoundingClientRect());

  // hide the real collage photos and any remaining reel thumbnails while
  // the clones fly
  collagePhotoEls.forEach(el => el.style.opacity = '0');
  const hiddenSlots = reelSlotEls.slice(0, photos.length);
  hiddenSlots.forEach(el => el.style.opacity = '0');

  // one clone per captured from-rect (the reel may already be gone, in
  // which case there is nothing to fly and the collage just appears)
  const clones = fromRects.map((rect, i) => createAnimationClone(photos[i].dataUrl, rect));

  await animationController.withAnimationTracking(async () => {
    const animations = clones.map((clone, i) => animateCloneToRect(clone, fromRects[i], toRects[i], 300));
    await animationController.safeAwaitAnimations(animations);
  });

  clones.forEach(clone => clone.remove());
  collagePhotoEls.forEach(el => el.style.opacity = '');
  hiddenSlots.forEach(el => el.style.opacity = '');
  return true;
}

/**
 * What one scanner page lends the two orchestrators below: its elements, its
 * captured photos, and the two things only a page can do — say whether it has
 * been navigated away from, and ask for a repaint.
 *
 * These are functions rather than values because the elements come from
 * `@ViewChild` getters that resolve late and can change between animation
 * frames, and because `viewDestroyed` and `markForCheck` are protected on the
 * page's base class and cannot be read from out here.
 */
export interface ScannerPageAnimationHooks {
  animationController: AnimationController;
  /** the live camera preview element, or null when the camera is not showing */
  cameraElement: () => HTMLElement | null;
  /** the reel's thumbnail slot elements, in order, including empty slots */
  reelSlotElements: () => HTMLElement[];
  /** the collage's photo elements, in order */
  collagePhotoElements: () => HTMLElement[];
  /** the photos captured so far */
  capturedPhotos: () => { dataUrl: string }[];
  /** true once the user has navigated away from the page */
  isViewDestroyed: () => boolean;
  markForCheck: () => void;
}

/** When the collage transition stops the native camera. */
export enum CameraStopOrdering {
  /** wait for the camera teardown, then switch to the collage */
  BeforeCollage = 'before-collage',
  /**
   * start the teardown and switch to the collage without waiting for it. Only
   * the food-label scanner does this. `design-docs/scanner-shared-components.md`
   * records the ordering as a deliberate per-scanner difference, so it is spelt
   * out here rather than quietly converged.
   */
  AlongsideCollage = 'alongside-collage',
}

/**
 * Fly the photo just captured from the camera preview into its reel slot.
 * Every multi-photo scanner page calls this after adding a photo.
 */
export async function animateCapturedPhotoToReelAsync(
  hooks: ScannerPageAnimationHooks,
  slotIndex: number,
  dataUrl: string,
) {
  await animationFrame();
  if (hooks.isViewDestroyed()) return;

  await animateSnapToReelAsync(hooks.animationController, {
    cameraEl: hooks.cameraElement(),
    slotEl: hooks.reelSlotElements()[slotIndex] ?? null,
    dataUrl,
  });
}

/**
 * Stop the camera and fly the reel thumbnails to their collage positions.
 * Every multi-photo scanner page calls this as analysis starts.
 *
 * The reel rects are captured BEFORE the view switches, because the reel can
 * leave the DOM once the collage appears. When the collage elements don't match
 * the photos the collage simply appears with no fly animation.
 */
export async function animateCollageTransitionAsync(
  hooks: ScannerPageAnimationHooks,
  options: {
    /** whether the camera preview is currently running */
    isCameraActive: boolean;
    /** tear the native camera down */
    stopCameraAsync: () => Promise<void>;
    cameraStopOrdering: CameraStopOrdering;
  },
) {
  if (!options.isCameraActive && hooks.capturedPhotos().length === 0) return;
  await animationFrame();
  if (hooks.isViewDestroyed()) return;

  // capture reel positions for captured photos only (not empty slots) — must
  // happen BEFORE the view switches, the reel may leave the DOM
  const photoCount = hooks.capturedPhotos().length;
  const fromRects = hooks.reelSlotElements()
    .slice(0, photoCount)
    .map(el => el.getBoundingClientRect());

  // switch to collage view
  if (options.cameraStopOrdering === CameraStopOrdering.BeforeCollage) {
    await options.stopCameraAsync();
    if (hooks.isViewDestroyed()) return;
  } else {
    void options.stopCameraAsync();
  }
  hooks.markForCheck();
  await animationFrame();
  if (hooks.isViewDestroyed()) return;

  await animateReelToCollageAsync(hooks.animationController, {
    photos: hooks.capturedPhotos(),
    fromRects,
    collagePhotoEls: hooks.collagePhotoElements(),
    reelSlotEls: hooks.reelSlotElements(),
  });
  hooks.markForCheck();
}

// the fixed-position img clone that gets animated between rects
function createAnimationClone(dataUrl: string, sourceRect: DOMRect): HTMLImageElement {
  const clone = document.createElement('img');
  clone.src = dataUrl;
  clone.style.position = 'fixed';
  clone.style.left = `${sourceRect.left}px`;
  clone.style.top = `${sourceRect.top}px`;
  clone.style.width = `${sourceRect.width}px`;
  clone.style.height = `${sourceRect.height}px`;
  clone.style.objectFit = 'cover';
  clone.style.borderRadius = '4px';
  clone.style.zIndex = '200';
  clone.style.pointerEvents = 'none';
  clone.style.transformOrigin = 'top left';
  document.body.appendChild(clone);
  return clone;
}

// translate + scale the clone from its own rect to the target rect
function animateCloneToRect(clone: HTMLImageElement, from: DOMRect, to: DOMRect, durationMs: number): Animation {
  return clone.animate([
    { translate: '0 0', scale: '1 1' },
    {
      translate: `${to.left - from.left}px ${to.top - from.top}px`,
      scale: `${to.width / from.width} ${to.height / from.height}`,
    },
  ], { duration: durationMs, easing: 'ease-out', fill: 'forwards' });
}
